import {
  Badge,
  Card,
  Checkbox,
  Collapse,
  FileInput,
  Input,
  Loading,
  Modal,
  Select,
  Textarea
} from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useClient } from '@/stores/useClient.ts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDrives } from '@/stores/useDrives.ts'
import { IconArrowLeft, IconArrowRight, IconBrandWhatsapp, IconCheck, IconLock, IconPlus, IconTrash, IconX } from '@tabler/icons-react'
import { Album } from '@/models/album.ts'
import useCollapses from '@/hooks/useCollapses.tsx'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import { useHeyTheme } from '@/contexts/heyTheme.tsx'
import { HeyDrive, HeyDriveType } from '@/models/drive.ts'
import { ChromePicker } from 'react-color'
import DriveHeader from '@/components/DriveHeader.tsx'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import { defaultThemes, ThemeDetails } from '@/components/Theme/ThemeSelection.tsx'
import ThemeColors from '@/components/Theme/ThemeColors.tsx'
import QRCodeDesigner, { QRCodeShape } from '@/components/QRCodeDesigner.tsx'
import { captureException } from '@/controller/sentryHelper.ts'
import { getDriveDefaultsFor } from '@/controller/driveDefaults.ts'
import { Trans, useTranslation } from 'react-i18next'
import { getCurrentLanguageInfos } from '@/controller/languageHelper.ts'
import { FullDownload } from '@/models/fullDownload.ts'
import { AxiosError } from 'axios'
import { FullDownloadRequest, FullDownloadRequestStatus } from '@/models/fullDownloadRequest.ts'
import DownloadProgressBar from '@/components/Files/DownloadProgressBar.tsx'
import { getApiErrorCode } from '@/controller/apiErrors.ts'
import { toast, ToastContainer } from 'react-toastify'
import { getAlbumURL, getAPIUrl, getAuthenticatedDriveURL, getDriveURL, getHomescreenPath } from '@/controller/driveHelper.ts'
import { shareDownloadURL } from '@/controller/updateHelper.ts'
import CopyableInput from '@/components/CopyableInput.tsx'
import ShareableInput from '@/components/ShareableInput.tsx'
import { DefaultFileFilter } from '@/contexts/fileFilter.tsx'
import DriveDeletionElement from '@/components/DriveDeletionElement.tsx'
import IOSHomescreenPreview from '@/components/IOSHomescreenPreview.tsx'
import {
  canPromptInstall,
  isIOSDevice,
  isRunningStandalone,
  requestInstall,
  shouldOfferAppInstall
} from '@/controller/pwaInstall.ts'
import useAlbumReorder from '@/hooks/useAlbumReorder.ts'

export default function SettingsPage() {
  const { driveId } = useParams<{ driveId: string }>()
  const { drives  } = useDrives()
  const drive = drives[driveId!]!
  const {profile} = useProfile()
  const { currentAlbum } = useFilesContext()!
  const imageModalRef = useRef<HTMLDialogElement>(null)
  const appIconModalRef = useRef<HTMLDialogElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isDeletingImage, setIsDeletingImage] = useState(false)
  const { updateAlbum } = useClient()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  if (!profile) {
    return <>Loading</>
  }

  const isDriveOwner = profile!.username === drive.author

  function onChangeImageClick() {
    imageModalRef.current?.showModal()
  }

  function onImageUploadClick() {
    console.log('click', imageInputRef.current)
    imageInputRef.current!.click()
  }

  async function onImageFileSelect(e: any) {
    console.log('ay', e)
    if (isUploadingFile) {
      return
    }

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setIsUploadingFile(true)
      try {
        const data = new FormData()
        data.append('thumbnail', file)
        await updateAlbum(currentAlbum!.id, data)
        await queryClient.invalidateQueries({
          queryKey: ['drive', drive.id],
        })
        // Show app icon modal if not shown before
        if (shouldShowAppIconModal(drive.id)) {
          appIconModalRef.current?.showModal()
        }
      } catch (e) {
        alert('Error: ' + JSON.stringify(e))
        console.error('error uploading new image', e)
      } finally {
        setIsUploadingFile(false)
        imageModalRef.current?.close()
      }
    }
  }

  async function onDeleteImageClick() {
    if (isDeletingImage) {
      return
    }

    if (confirm(t('common.deleteRly'))) {
      setIsDeletingImage(true)
      try {
        await updateAlbum(currentAlbum!.id, { thumbnail: null })
        await queryClient.invalidateQueries({
          queryKey: ['drive', drive.id],
        })
      } catch (e) {
        alert('Error: ' + JSON.stringify(e))
        console.error('error deleting image', e)
      } finally {
        setIsDeletingImage(false)
        imageModalRef.current?.close()
      }
    }
  }

  if (drive.is_deletion_state) {
    return <div>
      <DriveHeader />
      <DriveDeletionElement drive={drive} />
    </div>
  }

  return <>
    <DriveHeader onChangeImageClick={onChangeImageClick} />
    <div className='container max-w-[40rem] mx-auto px-4 flex flex-col items-center gap-2'>
      <ProfileSettings />
      { isDriveOwner && currentAlbum && drive && <DriveSettings appIconModalRef={appIconModalRef} onChangeImageClick={onChangeImageClick} /> }
    </div>
    <input type='file' ref={imageInputRef} className='hidden' accept='image/jpeg,image/png' onInput={onImageFileSelect} />
    <Modal ref={imageModalRef} responsive className='bg-neutral'>
      <Modal.Header>
        {t('common.changeImage')}
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5" disabled={isUploadingFile || isDeletingImage}>✕
          </button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col items-center gap-3'>
          <Button disabled={isUploadingFile || isDeletingImage} loading={isUploadingFile} color='primary' onClick={onImageUploadClick}>
            {t('common.uploadImage')}
          </Button>
          <Button disabled={isUploadingFile || isDeletingImage || currentAlbum?.thumbnail == null} loading={isDeletingImage} color='ghost' size='sm' onClick={onDeleteImageClick}>
            {t('common.removeImage')}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
    <AppIconModal modalRef={appIconModalRef} thumbnailUrl={currentAlbum?.thumbnail ?? null} />
  </>
}

interface DriveSettingsProps {
  appIconModalRef: RefObject<HTMLDialogElement>
  onChangeImageClick: () => void
}

function DriveSettings({ appIconModalRef, onChangeImageClick }: DriveSettingsProps) {
  const { driveId } = useParams<{ driveId: string }>()
  const { drives, setDrive, setCurrentDrive } = useDrives()
  const drive = drives[driveId!]!
  const { currentAlbum: selectedAlbum } = useFilesContext()!
  const { updateDrive, updateAlbum, createAlbum, deleteAlbum } = useClient()
  const queryClient = useQueryClient()

  // const { albums } = useAlbums()
  const [driveTitle, setDriveTitle] = useState(drive.title)
  const [customLogo, setCustomLogo] = useState<File | null>(null)
  const [isSavingGeneral, setIsSavingGeneral] = useState(false)
  const [isSavingAlbum, setIsSavingAlbum] = useState(false)
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const [currentAlbum, setCurrentAlbum] = useState(drive.albums.find(album => selectedAlbum && album.id === selectedAlbum.id) || drive.albums.find(album => album.is_primary) || drive.albums[0])
  const [isCreatingNewAlbum, setIsCreatingNewAlbum] = useState(false)
  const { collapse: outerCollapse, selectedCollapse: selectedOuterCollapse } = useCollapses()
  const STORAGE_ADMIN_HINT_KEY = 'hide_hint_is_admin'
  const [showAdminHint, setShowAdminHint] = useState(localStorage.getItem(STORAGE_ADMIN_HINT_KEY) != 'true')
  const [sorting, setSorting] = useState(drive.default_files_order)
  const { t } = useTranslation()

  // Album that should become the selection as soon as the drive lists it
  // (a freshly created one – the refetch may land before or after the response).
  const pendingSelectAlbumId = useRef<string | null>(null)

  // Keep the edited album in sync with the store (e.g. `is_primary` after a reorder).
  // Same id → same `key` on AlbumEdit, so unsaved form input survives.
  useEffect(() => {
    const pending = pendingSelectAlbumId.current && drive.albums.find(a => a.id === pendingSelectAlbumId.current)
    if (pending) {
      pendingSelectAlbumId.current = null
      setIsCreatingNewAlbum(false)
      setCurrentAlbum(pending)
      return
    }
    const fresh = currentAlbum && drive.albums.find(a => a.id === currentAlbum.id)
    if (fresh && fresh !== currentAlbum) {
      setCurrentAlbum(fresh)
    }
  }, [drive.albums])

  // Album order: writes straight into the drive store (optimistic), the server
  // response then brings the final `order` / `is_primary` – see useAlbumReorder.
  const applyAlbums = useCallback((albums: Album[]) => {
    const state = useDrives.getState()
    const latest = state.drives[drive.id]
    if (!latest) return
    const next = { ...latest, albums }
    setDrive(next)
    if (state.currentDrive?.id === drive.id) {
      setCurrentDrive(next)
    }
  }, [drive.id, setDrive, setCurrentDrive])

  const { moveAlbum, isReordering } = useAlbumReorder({
    driveId: drive.id,
    albums: drive.albums,
    applyAlbums,
    onError: () => alert(t('common.errorReorderAlbums')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drive', drive.id] }),
  })

  // "Sortierung" button next to the album title (same look as the gallery's
  // "auswählen" toggle): only then every album shows its ◀ ▶ arrows in the
  // album row. Pointless with a single album.
  const canReorder = drive.albums.length > 1
  const [isSortingAlbums, setIsSortingAlbums] = useState(false)

  async function onMoveAlbum(album: Album, delta: number) {
    if (isReordering) return
    const previousPrimaryId = drive.albums[0]?.id
    await moveAlbum(album.id, delta)
    const first = useDrives.getState().drives[drive.id]?.albums[0]
    if (first && first.is_primary && first.id !== previousPrimaryId) {
      toast(t('common.isNowMainAlbum', { title: first.title }), { closeButton: true, autoClose: 3000 })
    }
  }

  async function onGeneralSaveClick() {
    if (isSavingGeneral) {
      return
    }

    setIsSavingGeneral(true)
    try {
      const data = new FormData()
      data.append('title', driveTitle)
      data.append('default_files_order', sorting)
      if (customLogo) {
        data.append('custom_logo', customLogo)
      }

      await updateDrive(drive.id, data)
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
    } catch (e) {
      alert('Error saving: ' + JSON.stringify(e))
      console.error(e)
    } finally {
      setIsSavingGeneral(false)
    }
  }

  function formDataForAlbumPartial(albumData: Partial<Album>) {
    const data = new FormData()
    data.append('title', albumData.title!)
    data.append('description', albumData.description!)
    data.append('is_private', albumData.is_private ? 'true' : 'false')
    data.append('is_upload_disabled', albumData.is_upload_disabled ? 'true' : 'false')
    data.append('is_unlisted', albumData.is_unlisted ? 'true' : 'false')
    data.append('is_games_disabled', albumData.is_games_disabled ? 'true' : 'false')
    data.append('is_max_media_per_guest', albumData.is_max_media_per_guest ? 'true' : 'false')
    data.append('is_max_media_also_game', albumData.is_max_media_also_game ? 'true' : 'false')
    data.append('is_guestbook_private', albumData.is_guestbook_private ? 'true' : 'false')
    data.append('is_guestbook_upload_disabled', albumData.is_guestbook_upload_disabled ? 'true' : 'false')
    data.append('is_guestbook_disabled', albumData.is_guestbook_disabled ? 'true' : 'false')
    data.append('max_media_per_guest', ''+albumData.max_media_per_guest)
    data.append('default_files_order', albumData.default_files_order!)
    if (albumData.thumbnail !== undefined) {
      data.append('thumbnail', albumData.thumbnail!)
    }

    return data
  }

  function onCustomLogoSelect(e: any) {
    if (!e.target.files || !e.target.files[0]) {
      setCustomLogo(null)
    } else {
      setCustomLogo(e.target.files[0])
    }
  }

  async function onAlbumSaveClick(album: Album, albumData: Partial<Album>) {
    if (isSavingAlbum) {
      return
    }

    setIsSavingAlbum(true)
    try {
      const updatedData = formDataForAlbumPartial(albumData)

      await updateAlbum(album.id, updatedData)
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
      // Show app icon modal if primary album and thumbnail was changed
      if (album.is_primary && albumData.thumbnail && shouldShowAppIconModal(drive.id)) {
        appIconModalRef.current?.showModal()
      }
    } catch (e) {
      alert('Error saving: ' + JSON.stringify(e))
      console.error(e)
    } finally {
      setIsSavingAlbum(false)
    }
  }

  async function onAlbumDeleteConfirm(album: Album) {
    if (isDeletingAlbum) {
      return
    }

    setIsDeletingAlbum(true)
    try {

      await deleteAlbum(album.id)
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
      // Fallbach after deleting
      setIsCreatingNewAlbum(true)
    } catch (e: any) {
      console.error('error deleting', e)
      if (e && e.response && e.response.status === 409) {
        alert(t('error.stillImages'))
      } else {
        alert('Error deleting: ' + JSON.stringify(e))
      }
    } finally {
      setIsDeletingAlbum(false)
    }
  }

  async function onAlbumCreate(albumData: Partial<Album>) {
    if (isSavingAlbum) {
      return
    }

    try {
      const data = formDataForAlbumPartial(albumData)

      const newAlbumRes = await createAlbum(drive.id, data)
      const newAlbum: Album = newAlbumRes.data
      // Jump straight to the new album: leave the "new" form and select it. Done
      // via the sync effect, which fires once the refetched drive lists the album –
      // so it works no matter when the store update lands.
      pendingSelectAlbumId.current = newAlbum.id
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
      const listed = useDrives.getState().drives[drive.id]?.albums.find(a => a.id === newAlbum.id)
      if (listed) {
        pendingSelectAlbumId.current = null
        setIsCreatingNewAlbum(false)
        setCurrentAlbum(listed)
      }
    } catch (e) {
      alert('Error creating album: ' + JSON.stringify(e))
      console.error(e)
    } finally {
      setIsSavingAlbum(false)
    }
  }

  function onHideAdminHintClick() {
    setShowAdminHint(false)
    localStorage.setItem(STORAGE_ADMIN_HINT_KEY, 'true')
  }

  return <>
    <h2 className='text-3xl font-serif my-4 mb-5'>
      {t('common.settings')}
    </h2>

    { !drive.is_demo && showAdminHint && <Card className='mb-2 text-center'>
      <Card.Body className='p-3'>
        {t('common.youSeeAdmin')}
        <Button onClick={onHideAdminHintClick} className='ms-2' color='primary' size='sm'>{t('common.hideHint')}</Button>
      </Card.Body>
    </Card> }

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('general').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('general').onClick}>
        {t('common.generalSettings')}
      </Collapse.Title>
      <Collapse.Content>
        <div className='flex flex-col gap-3'>
          {
            drive.drive_type === HeyDriveType.COMPANY && <>
              <label className="form-control w-full max-w-xs">
                <span className="label-text">{t('common.driveLogo')}</span>
                <FileInput className='w-full' accept='image/jpeg,image/png,image/svg+xml' onInput={onCustomLogoSelect} />
              </label>
            </>
          }
          <label className="form-control w-full max-w-xs">
            <span className="label-text">{t('common.eventName')}</span>
            <Textarea value={driveTitle} onChange={e => setDriveTitle(e.target.value)}/>
          </label>
          { !drive.is_demo && <>
            <label className='form-control'>
              <span className="label-text">{t('common.standardSorting')}</span>
              <div>
                {/* @ts-ignore */}
                <Select size='sm' value={sorting || DefaultFileFilter.sort} onInput={(e) => setSorting(e.currentTarget.value)}>
                  <Select.Option value='-created_at'>
                    {t('common.timeOfUpload')} ({t('common.descending')})
                  </Select.Option>
                  <Select.Option value='created_at'>
                    {t('common.timeOfUpload')} ({t('common.ascending')})
                  </Select.Option>
                  <Select.Option value='-file_creation'>
                    {t('common.timeOfCapture')} ({t('common.descending')})
                  </Select.Option>
                  <Select.Option value='file_creation'>
                    {t('common.timeOfCapture')} ({t('common.ascending')})
                  </Select.Option>
                </Select>
              </div>
            </label>
          </>}
          <p>
            {t('common.moreSettingsAlbum')}
          </p>
          <Button disabled={isSavingGeneral} loading={isSavingGeneral} className='mt-5' color='primary'
                  onClick={onGeneralSaveClick}>{t('common.save')}</Button>
        </div>
      </Collapse.Content>
    </Collapse>

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('theme').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('theme').onClick}>
      {t('common.colors')}
      </Collapse.Title>
      <Collapse.Content>
        <ThemeSettings drive={drive} />
      </Collapse.Content>
    </Collapse>


    <Collapse className='collapse-arrow bg-secondary w-full' open={outerCollapse('albums').open}>
      <Collapse.Title className='font-semibold w-full' onClick={outerCollapse('albums').onClick}>
        {t('common.albums')}
      </Collapse.Title>
      <Collapse.Content>
        <div className='flex flex-col w-[80vw] max-w-[36rem] gap-3'>
          <div className='flex flex-row items-center justify-between gap-3'>
            <h3 className='text-xl font-semibold'>{t('common.albums')}</h3>
            {canReorder && (
              <Button fullRounded size='sm' color='primary' type='button'
                      onClick={() => setIsSortingAlbums(!isSortingAlbums)}>
                {(isSortingAlbums ? t('common.done') : t('common.sorting')).toLowerCase()}
              </Button>
            )}
          </div>
          <AlbumSelection
            drive={drive}
            selectedAlbum={isCreatingNewAlbum ? null : currentAlbum}
            setSelectedAlbum={(a) => {
              setIsCreatingNewAlbum(false)
              setCurrentAlbum(a)
            }}
            showScrollbar
            renderAlbumFooter={isSortingAlbums && !isCreatingNewAlbum ? (album, index) => (
                <div className='flex flex-row items-center justify-center gap-3 mt-2' onClick={e => e.stopPropagation()}>
                  <Button size='sm' shape='circle' color='ghost' type='button'
                          title={t('common.moveAlbumForward')}
                          disabled={isReordering || index <= 0}
                          onClick={() => onMoveAlbum(album, -1)}>
                    <IconArrowLeft size={20} />
                  </Button>
                  <Button size='sm' shape='circle' color='ghost' type='button'
                          title={t('common.moveAlbumBackward')}
                          disabled={isReordering || index >= drive.albums.length - 1}
                          onClick={() => onMoveAlbum(album, 1)}>
                    <IconArrowRight size={20} />
                  </Button>
                </div>
            ) : undefined}
            prependChild={(
              <div className='overflow-hidden inline-flex flex-col w-[6rem] flex-shrink-0 mr-3 items-center'
                   onClick={() => setIsCreatingNewAlbum(true)}>
                <div
                  className='rounded-full w-[4rem] h-[4rem] mb-2 bg-primary inline-flex items-center justify-center relative'>
                  <IconPlus className='m-0 p-0 text-neutral absolute' size={40}/>
                </div>
                <div className={
                  'rounded-xl text-center text-sm px-2 w-full py-1 border-primary overflow-hidden overflow-ellipsis whitespace-nowrap ' + (isCreatingNewAlbum ? 'bg-primary text-neutral' : '')
                }>
                  {t('common.new')}
                </div>
              </div>
            )}
          />
        </div>
        <div className='flex flex-col mt-5'>
          {
          isCreatingNewAlbum && <>
              <h3 className='text-xl font-semibold'>{t('common.createNewAlbum')}</h3>
              <AlbumEdit
                drive={drive}
                onSubmit={onAlbumCreate}
                album={{
                  title: '',
                  description: '',
                  max_media_per_guest: 10,
                }}
                isSaving={isSavingAlbum}
              />
            </>
          }
          {
            !isCreatingNewAlbum && currentAlbum &&  <>
              <h3 className='text-xl font-semibold'>{t('common.editAlbum')}</h3>
              <AlbumEdit
                drive={drive}
                originalAlbum={currentAlbum}
                key={currentAlbum.id}
                onSubmit={data => onAlbumSaveClick(currentAlbum, data)}
                album={currentAlbum}
                isSaving={isSavingAlbum}
                isDeleting={isDeletingAlbum}
                onDeleteConfirm={onAlbumDeleteConfirm}
                appIconModalRef={appIconModalRef}
              />
            </>
          }
        </div>
      </Collapse.Content>
    </Collapse>

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('qrcode').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('qrcode').onClick}>
        {t('common.qrCode')}
      </Collapse.Title>
      <Collapse.Content>
        <QRCodeSettings drive={drive} />
      </Collapse.Content>
    </Collapse>

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('liveshow').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('liveshow').onClick}>
        {t('common.liveshow')}
      </Collapse.Title>
      <Collapse.Content>
        <Liveshow drive={drive} />
      </Collapse.Content>
    </Collapse>

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('cards').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('cards').onClick}>
        {t('common.cardDesigns')}
      </Collapse.Title>
      <Collapse.Content>
        <Cards drive={drive} />
      </Collapse.Content>
    </Collapse>

    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('download').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('download').onClick}>
        {t('common.fullDownload')}
      </Collapse.Title>
      <Collapse.Content>
        <FullDownloadSection drive={drive} open={selectedOuterCollapse === 'download'} />
      </Collapse.Content>
    </Collapse>

    { !drive.is_demo && <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('duration').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('duration').onClick}>
        {t('common.storageDuration')}
      </Collapse.Title>
      <Collapse.Content>
        <StorageDurationSection drive={drive} open={selectedOuterCollapse === 'duration'} />
      </Collapse.Content>
    </Collapse> }

    {shouldOfferAppInstall() && <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('homescreen').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('homescreen').onClick}>
        {t('common.addToHomescreen')}
      </Collapse.Title>
      <Collapse.Content>
        <AppIconSection
          thumbnailUrl={selectedAlbum?.thumbnail ?? null}
          onChangeImageClick={onChangeImageClick}
        />
      </Collapse.Content>
    </Collapse>}


    <Collapse className='collapse-arrow bg-secondary' open={outerCollapse('help').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('help').onClick}>
        {t('common.helpSupport')}
      </Collapse.Title>
      <Collapse.Content>
        <HelpSupport drive={drive} />
      </Collapse.Content>
    </Collapse>
  </>
}

interface AlbumEditProps {
  drive: HeyDrive
  onSubmit: (album: Partial<Album>) => void
  album: Partial<Album>
  isSaving: boolean
  originalAlbum?: Album
  isDeleting?: boolean
  onDeleteConfirm?: (originalAlbum: Album) => void
  appIconModalRef?: React.RefObject<HTMLDialogElement | null>
}

function AlbumEdit({ drive, onSubmit, album, isSaving, isDeleting, onDeleteConfirm, originalAlbum, appIconModalRef }: AlbumEditProps) {
  const { t } = useTranslation()
  const { collapse: albumCollapse} = useCollapses()
  const deleteModalRef = useRef<HTMLDialogElement>(null)
  const [albumData, setAlbumData] = useState<Partial<Album>>({
    title: album.title,
    description: album.description,
    is_private: album.is_private,
    is_upload_disabled: album.is_upload_disabled,
    is_unlisted: album.is_unlisted,
    is_games_disabled: album.is_games_disabled,
    is_max_media_also_game: album.is_max_media_also_game,
    is_max_media_per_guest: album.is_max_media_per_guest,
    max_media_per_guest: album.max_media_per_guest,
    is_guestbook_private: album.is_guestbook_private,
    is_guestbook_disabled: album.is_guestbook_disabled,
    is_guestbook_upload_disabled: album.is_guestbook_upload_disabled,
    default_files_order: album.default_files_order,
    thumbnail: undefined,
  })

  function updateAlbumDatas(data: Partial<Album>) {
    setAlbumData({
      ...albumData,
      ...data,
    })
  }

  function onFileSelect(e: any) {
    if (!e.target.files || !e.target.files[0]) {
      updateAlbumDatas({
        thumbnail: undefined,
      })
    }

    updateAlbumDatas({
      thumbnail: e.target.files[0],
    })
  }

  function onSubmitClick(e: any) {
    e.preventDefault()
    e.stopPropagation()
    onAlbumSaveClick()
  }

  function onAlbumSaveClick() {
    console.log('thumbnail', albumData)
    onSubmit(albumData)
  }

  const albumUrl = originalAlbum ? getAlbumURL(drive.id, originalAlbum.id) : null

  async function onAlbumQRCodeDownloadClick() {
    const qrCodeUrl = getAPIUrl(`api/album/${originalAlbum!.id}/download_qr_code/`)
    await shareDownloadURL(qrCodeUrl, 'FridaySnap-Album-QR-Code.png', () => {})
  }

  return <>
    <form className='flex flex-col gap-3' onSubmit={onSubmitClick}>
      <label className="form-control w-full max-w-xs">
        <span className="label-text">{t('common.name')}</span>
        <Input maxLength={100} value={albumData.title} required
               onChange={e => updateAlbumDatas({title: e.target.value})}/>
      </label>
      <label className="form-control w-full max-w-xs">

        <span className="label-text">{t('common.previewImage')}</span>
        <div className='mt-1'>
          <div className='flex flex-row gap-5 items-center'>
            {(albumData.thumbnail || album.thumbnail) &&
              <div className='w-[5rem] h-[5rem] mb-2 bg-cover bg-center rounded-full'
                // @ts-ignore*
                   style={{backgroundImage: `url(${albumData.thumbnail ? URL.createObjectURL(albumData.thumbnail) : album.thumbnail})`}}></div>}
            {/*{albumData.thumbnail && <div className='w-[5rem] h-[5rem] mb-2' src={''''''''''}></div>}*/}
            {/*{album.thumbnail && !albumData.thumbnail && <img className='w-[5rem] h-[5rem] mb-2' src={album.thumbnail}/>}*/}
            {/*{ (album.thumbnail || albumData.thumbnail !== null) && <Button onClick={onPreviewImageRemove} color='neutral' size='sm'>Bild entfernen</Button> }*/}
          </div>
          {/*<Button color='neutral' size='sm' onClick={}>Auswählen</Button>*/}
          <FileInput className='w-full' accept='image/jpeg,image/png' onInput={onFileSelect}/>
        </div>
      </label>

      <label className="form-control w-full max-w-xs">
        <span className="label-text">{t('common.description')}</span>
        <Textarea maxLength={1024} value={albumData.description}
                  onChange={e => updateAlbumDatas({description: e.target.value})}/>
      </label>
      <label>
        <div className='flex flex-row gap-3 items-center'>
          <Checkbox color='primary' checked={!albumData.is_games_disabled}
                    onChange={() => updateAlbumDatas({is_games_disabled: !albumData.is_games_disabled})}></Checkbox>
          <div>
            {t('common.showGames')}
          </div>
        </div>
      </label>
      <h3 className='font-semibold text-xl'>{t('common.privacy')}</h3>
      <label>
        <div className='flex flex-row gap-3 items-center'>
          <Checkbox color='primary' checked={!albumData.is_private}
                    onChange={() => updateAlbumDatas({is_private: !albumData.is_private})}></Checkbox>
          <div>
            {t('common.guestsCanSeeAll')}
            <p className='text-sm mt-1'>
              {t('common.guestsCanSeeAllDesc')}
            </p>
          </div>
        </div>
      </label>
      <label>
        <div className='flex flex-row gap-3 items-center'>
          <Checkbox color='primary' checked={!albumData.is_upload_disabled}
                    onChange={() => updateAlbumDatas({is_upload_disabled: !albumData.is_upload_disabled})}></Checkbox>
          <div>
            {t('common.canUpload')}
          </div>
        </div>
      </label>
      {!album.is_primary && <label>
        <div className='flex flex-row gap-3 items-center'>
          <Checkbox color='primary' checked={!albumData.is_unlisted}
                    onChange={() => updateAlbumDatas({is_unlisted: !albumData.is_unlisted})}></Checkbox>
          <div>
            {t('common.thisAlbumIsListed')}
            <p className='text-sm mt-1'>
              {t('common.thisAlbumIsListedDesc')}
            </p>
          </div>
        </div>
      </label>}
      <h3 className='font-semibold text-xl'>{t('common.moreSettings')}</h3>
      <div className={'flex flex-col gap-3 ' + (!albumData.is_guestbook_disabled ? 'bg-neutral p-3 rounded-xl' : '')}>
        <label>
          <div className='flex flex-row gap-3 items-center'>
            <Checkbox color='primary' checked={!albumData.is_guestbook_disabled}
                      onChange={() => updateAlbumDatas({is_guestbook_disabled: !albumData.is_guestbook_disabled})}></Checkbox>
            <div>
              {t('common.guestbookEnabled')}
            </div>
          </div>
        </label>
        { !albumData.is_guestbook_disabled && <>
          <label>
            <div className='flex flex-row gap-3 items-center'>
              <Checkbox color='primary' checked={!albumData.is_guestbook_private}
                        onChange={() => updateAlbumDatas({is_guestbook_private: !albumData.is_guestbook_private})}></Checkbox>
              <div>
                {t('common.guestbookPublic')}
              </div>
            </div>
          </label>
          <label>
            <div className='flex flex-row gap-3 items-center'>
              <Checkbox color='primary' checked={!albumData.is_guestbook_upload_disabled}
                        onChange={() => updateAlbumDatas({is_guestbook_upload_disabled: !albumData.is_guestbook_upload_disabled})}></Checkbox>
              <div>
                {t('common.guestbookUploadEnabled')}
              </div>
            </div>
          </label>
        </> }
      </div>
      <div className={'flex flex-col gap-3 ' + (albumData.is_max_media_per_guest ? 'bg-neutral p-3 rounded-xl' : '')}>
        <label>
          <div className='flex flex-row gap-3 items-center'>
            <Checkbox color='primary' checked={albumData.is_max_media_per_guest}
                      onChange={() => updateAlbumDatas({is_max_media_per_guest: !albumData.is_max_media_per_guest})}></Checkbox>
            <div>
              {t('common.limitPerGuest')}
            </div>
          </div>
        </label>
        { albumData.is_max_media_per_guest && <>
          <label>
            <div className='flex flex-row gap-3 items-center'>
              <Checkbox color='primary' checked={albumData.is_max_media_also_game}
                        onChange={() => updateAlbumDatas({is_max_media_also_game: !albumData.is_max_media_also_game})}></Checkbox>
              <div>
                {t('common.limitPerGuestAlsoGame')}
                <p className='text-sm mt-1'>
                  {t('common.limitPerGuestAlsoGameDesc')}
                </p>
              </div>
            </div>
          </label>
          <label className="form-control w-full max-w-xs">
            <span className="label-text">
              {t('common.fileLimitPerGuest')}
            </span>
            <Input type='number' value={albumData.max_media_per_guest} required
                   onChange={(e => {
                     try {
                       const newLimit = parseInt(e.target.value)
                       updateAlbumDatas({ max_media_per_guest: newLimit })
                     } catch (e) {

                     }
                   })}
                 />
          </label>
        </> }
      </div>
      { !drive.is_demo && <>
        <label className='form-control'>
          <span className="label-text">{t('common.sorting')}</span>
          <div>
            {/* @ts-ignore */}
            <Select size='sm' value={albumData.default_files_order || DefaultFileFilter.sort} onInput={(e) => updateAlbumDatas({ default_files_order: e.currentTarget.value })}>
              <Select.Option value=''>
                {t('common.standardSorting')}
              </Select.Option>
              <Select.Option value='-created_at'>
                {t('common.timeOfUpload')} ({t('common.descending')})
              </Select.Option>
              <Select.Option value='created_at'>
                {t('common.timeOfUpload')} ({t('common.ascending')})
              </Select.Option>
              <Select.Option value='-file_creation'>
                {t('common.timeOfCapture')} ({t('common.descending')})
              </Select.Option>
              <Select.Option value='file_creation'>
                {t('common.timeOfCapture')} ({t('common.ascending')})
              </Select.Option>
            </Select>
          </div>
        </label>
      </>}
      {originalAlbum && <>
        {/* App icon preview — phones with a browser that can install */}
        {shouldOfferAppInstall() && originalAlbum.thumbnail && (
          <div className='flex flex-col items-center gap-4 p-4 bg-secondary rounded-xl'>
            <h3 className='font-semibold text-lg'>{t('common.addToHomescreen')}</h3>
            <IOSHomescreenPreview
              thumbnailUrl={originalAlbum.thumbnail}
              onLinkClick={() => appIconModalRef?.current?.showModal()}
            />
          </div>
        )}
        <Collapse className='collapse-arrow bg-secondary' open={albumCollapse('info').open}>
          <Collapse.Title className='px-0 font-semibold' onClick={albumCollapse('info').onClick}>
            <h3 className='font-semibold text-xl'>{t('common.linkToAlbum')}</h3>
          </Collapse.Title>
          <Collapse.Content className='px-0'>
            <div className='flex flex-col gap-5'>

              <label className="form-control w-full max-w-xs">
                <span className="label-text">{t('common.linkToAlbum')}</span>
                <CopyableInput value={albumUrl!} />
              </label>
              { album.qr_code_image && <div className='flex flex-col gap-3'>
                <span className="label-text">{t('common.qrCode')}</span>
                { <img src={album.qr_code_image} className='w-[10rem]'/> }
                <div>
                  <Button color='primary' onClick={onAlbumQRCodeDownloadClick}>{t('common.download')}</Button>
                </div>
              </div> }
            </div>
          </Collapse.Content>
        </Collapse>
        <Collapse className='collapse-arrow bg-secondary' open={albumCollapse('delete').open}>
          <Collapse.Title className='px-0 font-semibold' onClick={albumCollapse('delete').onClick}>
            <h3 className='font-semibold text-xl'>{t('common.deleteAlbumQuestion')}</h3>
          </Collapse.Title>
          <Collapse.Content className='px-0'>
            <div className='flex flex-col gap-5'>
              {originalAlbum.is_primary && <p>{t('common.cantDeleteMainAlbum')}</p>}
              {!originalAlbum.is_primary && <>
                <p>{t('common.cantOnlyDeleteEmptyAlbums')}</p>
                <div className='flex'>
                  <Badge className='flex-1 py-1 h-[unset]' variant='outline' size='lg' color='error'
                         onClick={() => deleteModalRef.current!.showModal()}>{t('common.deleteAlbum')}</Badge>
                </div>
              </>}
            </div>
          </Collapse.Content>
        </Collapse>
      </>}

      <Button disabled={isSaving} loading={isSaving} className='mt-5' color='primary'>{t('common.save')}</Button>
    </form>
    {originalAlbum && <Modal ref={deleteModalRef} responsive>
      <Modal.Header className='text-center'>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconTrash size='20'/>
          </div>
          {t('common.deleteAlbum')}
        </div>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕
          </button>
        </form>
      </Modal.Header>
      <Modal.Body className='text-center flex flex-col gap-3'>
        <div className='flex flex-col gap-2 items-center'>
          <p className='mb-5'>
            {t('common.rlyDeleteAlbum')}
          </p>
          <Button disabled={isDeleting} loading={isDeleting} className='px-5' color='error'
                  onClick={() => onDeleteConfirm!(originalAlbum)}>{t('common.delete')}</Button>
          <form method="dialog">
            <Button disabled={isDeleting} color='ghost'>{t('common.cancel')}</Button>
          </form>
        </div>

      </Modal.Body>
    </Modal> }
  </>
}

function ProfileSettings() {
  const {profile, logout, authenticationToken} = useProfile()
  const [displayName, setDisplayName] = useState(profile!.display_name)
  const [isSaving, setIsSaving] = useState(false)
  const {updateProfile} = useClient()
  const queryClient = useQueryClient()
  const { collapse: outerCollapse } = useCollapses()
  const { driveId } = useParams<{ driveId: string }>()
  const { drives  } = useDrives()
  const drive = drives[driveId!]!
  const navigate = useNavigate()
  const accessModal = useRef<HTMLDialogElement>(null)
  const { t } = useTranslation()

  function onLogoutClick() {
    logout()
    navigate('..')
    setTimeout(() => window.location.reload(), 200)
  }

  async function onProfileSaveClick() {
    if (isSaving) {
      return
    }

    setIsSaving(true)

    try {
      await updateProfile(profile!.username, {display_name: displayName})
      await queryClient.invalidateQueries({
        queryKey: ['profile'],
      })
    } catch (e) {
      alert('Error saving profile ' + JSON.stringify(e))
    } finally {
      setIsSaving(false)
    }
  }

  function onShareAccessClick() {
    accessModal.current!.showModal()
  }

  return <>
    <Modal className='bg-neutral' responsive ref={accessModal}>
      <Modal.Header>
        Demo-Zugang teilen
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">
            <IconX />
          </button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <ToastContainer />
        <div className='flex flex-col gap-5'>
          <Card className='bg-secondary bg-opacity-25'>
            <Card.Body>
              <h3 className='font-bold text-xl flex flex-row gap-1 items-center'><IconLock size={20} /> Voller Zugang</h3>
              <p>Dieser Zugang erlaubt es, alle Einstellungen anzupassen und auch private Fotos einzusehen.</p>
              <ShareableInput value={getAuthenticatedDriveURL(driveId!, authenticationToken!)} />
            </Card.Body>
          </Card>
          <Card className='bg-secondary bg-opacity-25'>
            <Card.Body>
              <h3 className='font-bold text-xl flex flex-row gap-1 items-center'>Gäste Zugang</h3>
              <ShareableInput value={getDriveURL(driveId!)} />
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
    </Modal>
    <h2 className='text-3xl font-serif my-4'>{t('common.myProfile')}</h2>
    { drive.author == profile!.username && !drive.is_demo && <>
       {/*<div className='flex flex-row gap-2 items-center text-sm'>*/}
       {/*  <p className='opacity-50'>Vom Admin-Link eingeloggt</p>*/}
         <Button onClick={onLogoutClick} color='primary' variant='outline' size='sm'>{t('common.logout')}</Button>
       {/*</div>*/}
    </>}
    {
      drive.is_demo && <Button size='sm' color='primary' variant='outline' onClick={onShareAccessClick}>Demo-Zugang teilen</Button>
    }
    <Collapse className='mt-5 collapse-arrow bg-secondary' open={outerCollapse('name').open}>
      <Collapse.Title className='font-semibold' onClick={outerCollapse('name').onClick}>
        {t('common.name')}
      </Collapse.Title>
      <Collapse.Content>
        <div className='flex flex-col gap-3'>
          <Input value={displayName} onChange={e => setDisplayName(e.target.value)}/>
          <Button disabled={isSaving} loading={isSaving} className='mt-5' color='primary'
                  onClick={onProfileSaveClick}>
            {t('common.save')}
          </Button>
        </div>
      </Collapse.Content>
    </Collapse>
  </>
}

interface ThemeDetailsKeyData {
  display: string
  property: string
  color: string
}

function ThemeSettings({ drive }: { drive: HeyDrive }) {
  const [isSavingTheme, setIsSavingTheme] = useState(false)
  const { setTheme } = useHeyTheme()
  const { t } = useTranslation()

  const driveDefaults = getDriveDefaultsFor(drive.drive_type)
  const defaultTheme = {
    primary: '#' + driveDefaults.color_primary,
    neutral: '#' + driveDefaults.color_neutral,
    content: '#' + driveDefaults.color_content,
  }
  const [currentTheme, setCurrentTheme] = useState<ThemeDetails>(drive.color_primary ? {
    primary: '#' + drive.color_primary,
    neutral: '#' + drive.color_neutral,
    content: '#' + drive.color_content,
  } : defaultTheme)
  const { updateDrive } = useClient()
  const queryClient = useQueryClient()
  const modalRef = useRef<HTMLDialogElement>(null)
  const [currentModalData, setCurrentModalData] = useState<ThemeDetailsKeyData | undefined>(undefined)

  async function onSaveClick() {
    if (isSavingTheme) {
      return
    }

    setIsSavingTheme(true)
    try {
      await updateDrive(drive.id, {
        color_primary: currentTheme.primary.slice(1),
        color_neutral: currentTheme.neutral.slice(1),
        color_content: currentTheme.content.slice(1),
      })
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
    } catch (e: any) {
      console.error('error saving theme', e)
      alert('Error savin theme' + JSON.stringify(e.response))
    } finally {
      setIsSavingTheme(false)
    }
  }

  function onColorChangeClick(data: ThemeDetailsKeyData) {
    setCurrentModalData(data)
    modalRef.current?.showModal()
  }

  function onThemeSelect(theme: ThemeDetails) {
    setTheme(
      theme.primary,
      theme.neutral,
      theme.content,
    )
    setCurrentTheme(theme)
  }

  const themePreviews = defaultThemes.map((theme, i) => <ThemeColors key={i} theme={theme} onClick={() => onThemeSelect(theme)} />)

  return <div className='flex flex-col gap-3'>
    <Modal ref={modalRef} responsive className='bg-neutral'>
      <Modal.Header>
        {currentModalData?.display}
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕
          </button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col items-center'>
          {currentModalData && <ChromePicker
            disableAlpha
            // @ts-ignore
            color={currentTheme[currentModalData.property]}
              onChange={(c) => onThemeSelect({
                ...currentTheme,
                [currentModalData?.property]: c.hex,
              })}
            />
          }
          <Button disabled={isSavingTheme} loading={isSavingTheme} className='mt-5' color='primary' onClick={() => modalRef.current?.close()}>{t('common.done')}</Button>
        </div>
      </Modal.Body>
    </Modal>
    <h3 className='text-xl font-semibold'>{t('common.chooseExampleImages')}</h3>
    <div className='flex flex-row gap-2'>
      { themePreviews }
    </div>
    <h3 className='text-xl font-semibold'>{t('common.adaptColors')}</h3>
    <div className='flex flex-row gap-3'>
      <ThemeColors
        theme={currentTheme}
        big
        onPrimaryClick={() => onColorChangeClick({ display: t('common.primary'), property: 'primary', color: currentTheme.primary })}
        onNeutralClick={() => onColorChangeClick({ display: t('common.background'), property: 'neutral', color: currentTheme.neutral })}
        onContentClick={() => onColorChangeClick({ display: t('common.content'), property: 'content', color: currentTheme.content })}
      />
      <div className='inline-flex flex-col gap-2 p-2 rounded'>
        <div className='rounded-full h-[2rem] flex items-center' onClick={() => onColorChangeClick({ display: t('common.primary'), property: 'primary', color: currentTheme.primary })}>{t('common.primary')}</div>
        <div className='rounded-full h-[2rem] flex items-center' onClick={() => onColorChangeClick({ display: t('common.background'), property: 'neutral', color: currentTheme.neutral })}>{t('common.background')}</div>
        <div className='rounded-full h-[2rem] flex items-center' onClick={() => onColorChangeClick({ display: t('common.content'), property: 'content', color: currentTheme.content })}>{t('common.content')}</div>
      </div>
      <div className='inline-flex flex-col gap-2 p-2 rounded text-[#040404]'>
        <div className='rounded-full h-[2rem] py-[0.2rem] flex items-center underline cursor-pointer lowercase' onClick={() => onColorChangeClick({ display: t('common.primary'), property: 'primary', color: currentTheme.primary })}>🎨 {t('common.select')}</div>
        <div className='rounded-full h-[2rem] py-[0.2rem] flex items-center underline cursor-pointer lowercase' onClick={() => onColorChangeClick({ display: t('common.background'), property: 'neutral', color: currentTheme.neutral })}>🎨 {t('common.select')}</div>
        <div className='rounded-full h-[2rem] py-[0.2rem] flex items-center underline cursor-pointer lowercase' onClick={() => onColorChangeClick({ display: t('common.content'), property: 'content', color: currentTheme.content })}>🎨 {t('common.select')}</div>
      </div>
    </div>
    <Button disabled={isSavingTheme} loading={isSavingTheme} className='mt-5' color='primary'
            onClick={onSaveClick}>{t('common.save')}</Button>
  </div>
}

function FullDownloadSection({ drive, open }: { drive: HeyDrive, open: boolean }) {
  const { t } = useTranslation()
  const { getFullDownloads, getFullDownloadRequests, requestFullDownload } = useClient()
  const { data: fullDownloads, isLoading: isFullDownloadLoading, refetch: refetchDownloads } = useQuery({
    queryKey: ['drive', drive.id, 'downloads'],
    queryFn: async () => {
      const res = await getFullDownloads(drive)
      return res.data
    },
    enabled: open,
  })

  const { data: fullDownloadRequests, isLoading: isRequestsLoading, refetch: refetchRequests } = useQuery({
    queryKey: ['drive', drive.id, 'downloadRequests'],
    queryFn: async () => {
      const res = await getFullDownloadRequests(drive)
      return res.data
    },
    enabled: open,
    // Keep the progress of running requests up to date. The download server only
    // reports a handful of values, so polling faster than this gains nothing.
    refetchInterval: query => {
      const requests = query.state.data as FullDownloadRequest[] | undefined
      const hasRunning = requests?.some(request => request.status === FullDownloadRequestStatus.IN_PROGRESS
        || request.status === FullDownloadRequestStatus.TODO)
      return hasRunning ? 3000 : false
    },
  })

  const nonDoneDownloads = fullDownloadRequests && fullDownloadRequests.filter((downloadRequest: FullDownloadRequest) => downloadRequest.status !== FullDownloadRequestStatus.DONE)

  // The request list and the finished downloads are two independent endpoints.
  // A request that reaches `done` only disappears from the list above — the ZIP it
  // produced would stay invisible until the page is reloaded. So whenever a request
  // we were watching finishes, the downloads list is refetched once.
  const pendingRequestIdsRef = useRef<string[]>([])
  useEffect(() => {
    const requests = (fullDownloadRequests ?? []) as FullDownloadRequest[]
    const pendingIds = requests
      .filter(request => request.status !== FullDownloadRequestStatus.DONE
        && request.status !== FullDownloadRequestStatus.FAILED)
      .map(request => request.id)

    // Finished = was pending on the previous poll and is now done (or gone entirely,
    // e.g. because the backend dropped it after producing the download).
    const hasFinished = pendingRequestIdsRef.current.some(id => {
      if (pendingIds.includes(id)) return false
      const request = requests.find(r => r.id === id)
      return !request || request.status === FullDownloadRequestStatus.DONE
    })

    pendingRequestIdsRef.current = pendingIds

    if (hasFinished) {
      refetchDownloads()
    }
  }, [fullDownloadRequests])

  async function onRequestFullDownloadClick() {
    try {
      const downloadRequestRes = await requestFullDownload(drive)
      console.log('DownloadRequestResponse', downloadRequestRes)
      await refetchRequests()
    } catch (e) {
      if (e instanceof AxiosError) {
        if (!e.response) {
          captureException(e, 'requesting full download')
          alert('Failed requesting download. Retry later.')
        } else {
          if (e.response.status === 400) {
            // `response.data` may be an object (DRF) or a raw string — reading it
            // must never throw, otherwise the real message is lost.
            const errorCode = getApiErrorCode(e)
            if (errorCode === 'download_cooldown') {
              alert(t('common.downloadCooldown', { hours: 2 }))
              return
            }

            if (errorCode === 'download_already_requested') {
              alert(t('common.downloadAlreadyRequested'))
              return
            }

            if (errorCode === 'download_already_exists') {
              alert(t('common.downloadAlreadyExists'))
              return
            }

            console.error('Error creating request', errorCode)
          } else {
            captureException(e, 'requesting full download')
            alert('Failed requesting download. Retry later.')
          }
        }
      } else {
        alert('Failed requesting download. Retry later.')
        captureException(e, 'requesting full download')
      }
      console.error('nahh', e)
    }
  }

  function getStatusTextForRequest(status: FullDownloadRequestStatus) {
    if (status === FullDownloadRequestStatus.NEEDS_CONFIRMATION) {
      return t('common.downloadRequestNeedsConfirmation')
    }

    if (status === FullDownloadRequestStatus.TODO || status === FullDownloadRequestStatus.IN_PROGRESS) {
      return t('common.downloadRequestInProgress')
    }

    if (status === FullDownloadRequestStatus.FAILED) {
      return t('common.downloadRequestFailed')
    }
  }

  return <div className='flex flex-col gap-3'>
    <p>{t('common.youWantFullDownload')}</p>
    <h3 className='text-xl font-semibold'>{t('common.requestFullDownload')}</h3>
    { isFullDownloadLoading && isRequestsLoading && <Loading color='primary' /> }
    { fullDownloads && fullDownloads.length > 0 && <div className='flex flex-col gap-2 justify-center items-center w-full'>
      { fullDownloads.map((download: FullDownload) => <FullDownloadElement key={download.id} download={download} drive={drive} refetchDownloads={refetchDownloads} />) }
    </div> }
    { nonDoneDownloads && <div className='flex flex-col gap-2 justify-center items-center w-full'>
      { nonDoneDownloads.map((downloadRequest: FullDownloadRequest) => <div
        key={downloadRequest.id}
        className='bg-neutral rounded p-2 flex flex-col gap-2 w-full items-center'>
        <h4 className='font-bold'>{new Date(downloadRequest.created_at).toLocaleString()}</h4>
        <div className='text-center my-2'>
          <span><b>{t('common.status')}:</b> {getStatusTextForRequest(downloadRequest.status)}</span>
        </div>
        { downloadRequest.status === FullDownloadRequestStatus.IN_PROGRESS &&
          <DownloadProgressBar progress={downloadRequest.progress ?? 0} /> }
      </div>)}
    </div>}
    <Button color='primary' onClick={onRequestFullDownloadClick}>{t('common.createFullDownload')}</Button>
  </div>
}

function FullDownloadElement({ drive, download, refetchDownloads }: { drive: HeyDrive, download: FullDownload, refetchDownloads: () => Promise<any>}) {
  const { deleteFullDownload, getFullDownloadUrl } = useClient()
  const ref = useRef<HTMLDialogElement>(null);
  const [currentHours, setCurrentHours] = useState(1)
  const hoursPossible = [1, 2, 12, 24, 2*24, 3*24, 7*24]
  const { t } = useTranslation()

  const { data: downloadUrlData, isLoading: isUrlLoading } = useQuery({
    queryKey: ['drive', drive.id, 'downloads', currentHours],
    queryFn: async () => {
      const res = await getFullDownloadUrl(download, currentHours)
      return res.data as {download_url: string, expires_in_hours: string}
    },
  })

  async function onDeleteClick(download: FullDownload) {
    if (confirm(t('common.deleteThisFullDownload'))) {
      try {
        await deleteFullDownload(drive, download)
        await refetchDownloads()
      } catch (e) {
        console.error('Error deleting download', e)
        captureException(e, 'Error deleting full download')
      }
    }
  }

  function onShareClick() {
    ref.current!.showModal()
  }

  function renderHours(hours: number): string {
    if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat === 'undefined') {
      return `${hours}h`;
    }

    const lang = typeof navigator !== 'undefined' ? navigator.language : 'en-US';

    if (hours === 0) {
      const hourFormatter = new Intl.NumberFormat(lang, {
        style: 'unit',
        unit: 'hour',
        unitDisplay: 'long',
      });
      return hourFormatter.format(0);
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    const parts: string[] = [];

    if (days > 0) {
      const dayFormatter = new Intl.NumberFormat(lang, {
        style: 'unit',
        unit: 'day',
        unitDisplay: 'long',
      });
      parts.push(dayFormatter.format(days));
    }

    if (remainingHours > 0) {
      const hourFormatter = new Intl.NumberFormat(lang, {
        style: 'unit',
        unit: 'hour',
        unitDisplay: 'long',
      });
      parts.push(hourFormatter.format(remainingHours));
    }

    return parts.join(' ');
  }

  function onCopyLinkClick() {
    navigator.clipboard.writeText(downloadUrlData!.download_url).then(() => {
      toast(t('common.linkCopied'), {closeButton: true, autoClose: 2000})
    })
  }

  return <div className='bg-neutral rounded p-2 flex flex-col gap-2 w-full items-center'>
    <div className='relative w-full text-center'>
      <div className='absolute top-0 right-2 cursor-pointer' onClick={() => onDeleteClick(download)} color='ghost'><IconTrash /></div>
      <h4 className='font-bold'>{t('common.fullDownload')}</h4>
    </div>
    <p>{t('common.fullDownloadUntil', {date: new Date(download.created_at).toLocaleString()})}</p>
    {download.file_size && <div>
      <span><b>{t('common.fileSize')}:</b> {(download.file_size/1024/1024/1024).toFixed(4)} GB</span>
    </div>}
    <div className='text-center my-2 flex flex-row gap-2'>
      <a className='bg-primary text-neutral rounded p-2 font-semibold' href={download.file}>{t('common.download')}</a>
      <span className='bg-primary text-neutral rounded p-2 font-semibold' onClick={onShareClick}>
        {t('common.share')}
      </span>
      <Modal ref={ref} className='bg-neutral'>
        <ToastContainer />
        <Modal.Header>
          {t('common.shareDownload')}
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕
            </button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className='flex flex-col gap-3 items-center'>
            <b>{t('common.createDownloadLink')}</b>
            <label className="form-control flex flex-row w-full max-w-xs items-center gap-3 justify-center">
              <span className="label-text">{t('common.validFor')}</span>
                <Select value={''+currentHours} onChange={e => setCurrentHours(parseInt(e.currentTarget.value))}>
                  { hoursPossible.map(hours => <Select.Option key={''+hours} value={hours}>
                    { renderHours(hours) }
                  </Select.Option>)}
                </Select>
            </label>
            { isUrlLoading && <Loading color='primary' /> }
            { !isUrlLoading &&
              <>
                <div className='join flex flex-row'>
                  <Input color='primary' className='join-item' readOnly value={downloadUrlData ? downloadUrlData.download_url : ''} onFocus={e => e.target.select()} />
                  <Button color='primary' className='join-item' onClick={onCopyLinkClick}>{t('common.copyLink')}</Button>
                </div>
                <p>({t('common.validFor')}: {renderHours(currentHours)})</p>
              </>
            }
          </div>
        </Modal.Body>
      </Modal>
    </div>
  </div>
}

function StorageDurationSection({drive}: { drive: HeyDrive, open: boolean }) {
  const { t } = useTranslation()

  return <div className='flex flex-col gap-3'>
    <h3 className='text-xl font-semibold'>{t('common.storageDuration')}</h3>
    {!drive.usage_starts_at && <>
      <p>✅ {t('common.noStorageDesc')}</p>
      <p className='text-sm opacity-70'>{t('common.noStorageDescHint')}</p>
    </>}
    {drive.usage_starts_at && <>
      <p>{t('common.usageStarts', {date: new Date(drive.usage_starts_at).toLocaleDateString()})}</p>
    </>}
    {drive.expires_at && <>
      <p>{t('common.storageEnds', {date: new Date(drive.expires_at).toLocaleDateString()})}</p>
    </>}
    <Link to={`/d/${drive.id}/storage`}>
      <Button color='primary'>{t('common.extendStorageDuration')}</Button>
    </Link>
  </div>
}

function Liveshow({ drive }: { drive: HeyDrive }) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return <div className='flex flex-col gap-3'>
    <h3 className='text-xl font-semibold'>{t('common.liveshow')}</h3>
    <p>{t('common.liveshowExplanation')}</p>
    <p>{t('common.liveshowExplanation2')}</p>
    <p>{t('common.liveshowExplanation3')}</p>
    <Button color='primary' onClick={() => navigate(`/d/${drive.id}/liveshow`)}>{t('common.toLiveshow')}</Button>
  </div>
}

function Cards({ drive }: { drive: HeyDrive }) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return <div className='flex flex-col gap-3'>
    <h3 className='text-xl font-semibold'>{t('common.cardDesigns')}</h3>
    <p><Trans i18nKey='common.cardDesignsDesc' /></p>
    <Button color='primary' onClick={() => navigate(`/d/${drive.id}/cards`)}>{t('common.toCardDesigns')}</Button>
  </div>
}

function HelpSupport({drive}: { drive: HeyDrive }) {
  const langInfos = getCurrentLanguageInfos()
  const { t } = useTranslation()

  return <div className='flex flex-col gap-3 items'>
    <h3 className='text-xl font-semibold'>{t('common.helpAndSupport')}</h3>
    <p>
      <Trans i18nKey='common.helpAndSupportQuestion' />
    </p>
    <div className='flex flex-col gap-5 items-center mt-4'>
      <div>
        <Button color='success' className='text-[white] p-0 px-2'>
          <a className='w-full h-full flex flex-row items-center px-2 justify-center gap-1'
             href='https://wa.me/496987000170' target='_blank'
          >
            <IconBrandWhatsapp/>
            {t('common.openWhatsapp')}
          </a>
        </Button>
      </div>
      <p><b>E-Mail:</b> <a
        href={`mailto:${langInfos.email}?subject=${t('common.supportSubject', { id: drive.id })}&body=${t('common.supportBody')} `} className='text-primary underline'>{langInfos.email}</a></p>
    </div>
  </div>
}

function QRCodeSettings({drive}: { drive: HeyDrive }) {
  const [fg, setFg] = useState(`#${drive.qr_fg}`)
  const [bg, setBg] = useState(`#${drive.qr_bg}`)
  const [shape, setShape] = useState(drive.qr_shape)
  const [isSaving, setIsSaving] = useState(false)
  const { updateQrCode } = useClient()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  async function onQRCodeDownloadClick() {
    const qrCodeUrl = getAPIUrl(`api/drive/${drive.id}/download_qr_code/`)
    await shareDownloadURL(qrCodeUrl, 'FridaySnap-QR-Code.png', () => {})
  }

  async function onSaveClick() {
    if (isSaving) {
      return
    }

    setIsSaving(true)
    try {
      await updateQrCode(drive.id, {
        qr_fg: fg.substring(1),
        qr_bg: bg.substring(1),
        qr_shape: shape,
      })
      await queryClient.invalidateQueries({
        queryKey: ['drive', drive.id],
      })
    } catch (e: any) {
      console.error('error saving color', e)
      captureException(e, 'saving QR Code', () => ({ fg, bg, shape }))
      alert('Error savin color' + JSON.stringify(e.response))
    } finally {
      setIsSaving(false)
    }
  }

  return <div className='flex flex-col gap-3 items overflow-visible'>
    <h3 className='text-xl font-semibold'>{t('common.yourCurrentQrCode')}</h3>
    {
      drive.qr_code_image && <>
        <div className='flex flex-col gap-3'>
          <img crossOrigin='anonymous' src={drive.qr_code_image} className='w-[10rem]'/>
          <div>
            <Button color='primary' onClick={onQRCodeDownloadClick}>{t('common.download')}</Button>
          </div>
        </div>
      </>
    }
    {
      !drive.qr_code_image &&
      <p>{t('settings.qrCodeNotAvailable')}</p>
    }
    <h3 className='text-xl font-semibold mt-3'>{t('common.designQRCode')}</h3>
    <QRCodeDesigner fg={fg} setFg={setFg} bg={bg} setBg={setBg} shape={shape} setShape={setShape} svgBannerUrl='/fridaysnap-qrcode-banner.svg' shapes={{ [QRCodeShape.Rounded]: '/fridaysnap-qrcode.svg', [QRCodeShape.Square]: '/fridaysnap-qrcode-square.svg' }} />
    <Button onClick={onSaveClick} disabled={isSaving} loading={isSaving} color='primary'>{t('common.save')}</Button>
  </div>
}

const APP_ICON_LINK_SHOWN_KEY_PREFIX = 'app-icon-link-shown-'

function appIconShownKey(driveId: string): string {
  return `${APP_ICON_LINK_SHOWN_KEY_PREFIX}${driveId}`
}


interface AppIconModalProps {
  modalRef: RefObject<HTMLDialogElement>
  thumbnailUrl: string | null
}

export function AppIconModal({ modalRef, thumbnailUrl }: AppIconModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { driveId, albumId } = useParams<{ driveId: string, albumId?: string }>()
  const { authenticationToken } = useProfile()

  // Chromium (Android/Desktop) installs through its own dialog — no guide needed.
  const canInstallNatively = !isIOSDevice() && shouldOfferAppInstall()

  // Dismissing (X or Not-now) marks modal as permanently shown for this drive.
  function markShown() {
    if (driveId) {
      localStorage.setItem(appIconShownKey(driveId), 'true')
    }
  }

  function onCloseClick() {
    markShown()
    modalRef.current?.close()
  }

  function onNotNowClick() {
    markShown()
    modalRef.current?.close()
  }

  async function onInstallClick() {
    // Not asked again either way: accepting installs, declining is an answer too.
    markShown()
    // Close first — the browser dialog takes over from here.
    modalRef.current?.close()
    await requestInstall(t('common.installAppHint'))
  }

  function onLinkClick() {
    modalRef.current?.close()
    navigate(getHomescreenPath(driveId!, albumId, authenticationToken), { state: { showAddToHomescreen: true } })
  }

  return <Modal ref={modalRef} responsive className='bg-neutral'>
    <Modal.Header>
      {canInstallNatively ? t('common.installApp') : t('common.linkToHomescreen')}
      <button type='button' onClick={onCloseClick} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
    </Modal.Header>
    <Modal.Body>
      <div className='flex flex-col items-center gap-4 text-center'>
        <IOSHomescreenPreview thumbnailUrl={thumbnailUrl} showButton={false} />
        <p>{t('common.linkToHomescreenQuestion')}</p>
        <div className='flex flex-col gap-2 text-left text-sm'>
          <div className='flex flex-row items-center gap-2'>
            <IconCheck size={18} className='text-success shrink-0' />
            <span>{t('common.homescreenNoDownload')}</span>
          </div>
          <div className='flex flex-row items-center gap-2'>
            <IconCheck size={18} className='text-success shrink-0' />
            <span>{t('common.homescreenAlwaysAvailable')}</span>
          </div>
          <div className='flex flex-row items-center gap-2'>
            <IconCheck size={18} className='text-success shrink-0' />
            <span>{t('common.homescreenNoStorage')}</span>
          </div>
          <div className='flex flex-row items-center gap-2'>
            <IconCheck size={18} className='text-success shrink-0' />
            <span>{t('common.homescreenFastAccess')}</span>
          </div>
          <div className='flex flex-row items-center gap-2'>
            <IconCheck size={18} className='text-success shrink-0' />
            <span>{t('common.homescreenNoAppStore')}</span>
          </div>
        </div>
        <div className='flex flex-row gap-3'>
          <Button color='ghost' onClick={onNotNowClick}>{t('common.notNow')}</Button>
          {
            canInstallNatively
              ? <Button color='primary' onClick={onInstallClick}>{t('common.installAppButton')}</Button>
              : <Button color='primary' onClick={onLinkClick}>{t('common.toGuide')}</Button>
          }
        </div>
      </div>
    </Modal.Body>
  </Modal>
}

export function shouldShowAppIconModal(driveId: string | null | undefined): boolean {
  if (!driveId) return false
  if (!shouldOfferAppInstall()) return false
  // Skip if already installed as PWA
  if (isRunningStandalone()) return false
  if (localStorage.getItem(appIconShownKey(driveId)) === 'true') return false
  // iOS gets the manual guide; everywhere else only when the browser actually
  // offered us an install prompt — otherwise the button would lead nowhere.
  return isIOSDevice() || canPromptInstall()
}

interface AppIconSectionProps {
  thumbnailUrl: string | null
  onChangeImageClick: () => void
}

function AppIconSection({ thumbnailUrl, onChangeImageClick }: AppIconSectionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { driveId, albumId } = useParams<{ driveId: string, albumId?: string }>()
  const { authenticationToken } = useProfile()

  const canInstallNatively = !isIOSDevice() && shouldOfferAppInstall()

  function onGuideClick() {
    navigate(getHomescreenPath(driveId!, albumId, authenticationToken), { state: { showAddToHomescreen: true } })
  }

  return <div className='flex flex-col gap-4'>
    {/* App Icon Preview with Change Button side by side */}
    <div className='flex flex-row items-center gap-4'>
      <div className='flex flex-col items-center gap-1'>
        <div
          className='w-[4.5rem] h-[4.5rem] rounded-[1rem] bg-cover bg-center shadow-lg bg-base-300'
          style={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
        />
        <span className='text-sm'>App-Icon</span>
      </div>
      <Button color='primary' size='sm' onClick={onChangeImageClick}>{t('common.changeImage')}</Button>
    </div>

    <p>{t('common.linkToHomescreenQuestion')}</p>
    <div className='flex flex-col gap-2 text-left text-sm'>
      <div className='flex flex-row items-center gap-2'>
        <IconCheck size={18} className='text-success shrink-0' />
        <span>{t('common.homescreenNoDownload')}</span>
      </div>
      <div className='flex flex-row items-center gap-2'>
        <IconCheck size={18} className='text-success shrink-0' />
        <span>{t('common.homescreenAlwaysAvailable')}</span>
      </div>
      <div className='flex flex-row items-center gap-2'>
        <IconCheck size={18} className='text-success shrink-0' />
        <span>{t('common.homescreenNoStorage')}</span>
      </div>
      <div className='flex flex-row items-center gap-2'>
        <IconCheck size={18} className='text-success shrink-0' />
        <span>{t('common.homescreenFastAccess')}</span>
      </div>
      <div className='flex flex-row items-center gap-2'>
        <IconCheck size={18} className='text-success shrink-0' />
        <span>{t('common.homescreenNoAppStore')}</span>
      </div>
    </div>

    {/* Install (Chromium) or guide (iOS) button at the bottom */}
    {
      canInstallNatively
        ? <Button color='primary' onClick={() => { void requestInstall(t('common.installAppHint')) }}>{t('common.installAppButton')}</Button>
        : <Button color='primary' onClick={onGuideClick}>{t('common.toGuide')}</Button>
    }
  </div>
}
